<template>
  <section class="table-sampler-classification-chemical">
    <d-button id="updateButton" class="btn d-flex mb-2 ml-auto" @click.prevent="updateClassifications()">Atualizar Classificações</d-button>
    <d-form-row class="m-0 mb-1" v-for="(classification, index) in model.classifications" :key="index">
      <d-col cols="3">
        <label>Agente Químico</label>
        <input
          type="text"
          class="form-control"
          placeholder="Agente químico"
          v-model="classification.agent_chemical"
          required
        />
      </d-col>
      <d-col cols="auto">
        <label>Indice de risco</label>
         <d-select type="text" class="form-control" v-model="classification.index" required>
         <option v-if="classification.index && !indexOptions.includes(classification.index)" :value="classification.index">{{ classification.index }}</option>
          <option v-for="(option, index) in indexOptions" :value="option" :key="index">{{ option }}</option>
        </d-select>
      </d-col>
      <d-col cols="auto">
        <label>% LEO</label>
        <d-select type="text" class="form-control" v-model="classification.leo" required>
          <option v-if="classification.leo && !leoOptions.includes(classification.leo)" :value="classification.leo">{{ classification.leo }}</option>
          <option v-for="(option, index) in leoOptions" :value="option" :key="index">{{ option }}</option>
        </d-select>
      </d-col>
      <d-col cols>
        <label>Frequência de amostragem</label>
        <select type="text" class="form-control" v-model="classification.frequency" required>
          <option v-if="classification.frequency && !frequencyOptions.includes(classification.frequency)" :value="classification.frequency">{{ classification.frequency }}</option>
          <option v-for="(option, index) in frequencyOptions" :value="option" :key="index">{{ option }}</option>
        </select>
      </d-col>
    </d-form-row>
    <d-form-row class="m-0 mb-1">
      <d-col cols="12">
        <label>Recomendação da exposição</label>
        <textarea
          type="text"
          class="form-control"
          v-model="model.recommendation"
          required
        ></textarea>
      </d-col>
    </d-form-row>
  </section>
</template>

<script>
import Events from '_utils/events';
import { cloneDeep } from '_utils';
import { mapActions } from 'vuex';
import { CHEMICAL } from '_modules/settings/components/SettingsModel';
import { CHEMICAL_CLASSIFICATION, _LOAD_MODEL_ } from '../../SampleModel';

export default {
  name: 'Sample-Classification-Chemical',
  props: {
    classification: {
      type: Object,
      default: null,
    },
    result: {
      type: Array,
      default: null,
    },
  },
  data() {
    return {
      model: cloneDeep(CHEMICAL_CLASSIFICATION),
      isInitialLoad: true,
      indexOptions: [],
      leoOptions: [],
      frequencyOptions: [],
    };
  },
  computed: {
    chemicalSettings() {
      const { chemical } = this.$store.state.settings.results;
      return chemical || { ...CHEMICAL };
    },
  },
  methods: {
    ...mapActions(['getSettings']),
    setModel() {
      if (!this.classification) {
        this.model = cloneDeep(CHEMICAL_CLASSIFICATION);
        return;
      }

      if (this.isNewFormat()) {
        this.model = cloneDeep(this.classification);
      } else {
        this.convertFromOldFormat();
      }
    },
    isNewFormat() {
      return this.classification && Array.isArray(this.classification.classifications);
    },
    convertFromOldFormat() {
      const recommendation = this.classification.recommendation || this.classification.recomendation || '';
      this.model = cloneDeep({
        ...CHEMICAL_CLASSIFICATION,
        recommendation,
        classifications: [
          {
            agent_chemical: this.classification.agent_chemical || '',
            leo: this.classification.leo || null,
            index: this.classification.index || null,
            frequency: this.classification.frequency || null,
          },
        ],
      });
    },
    setSelectOptions() {
      const { risks } = this.chemicalSettings;
      const keys = Object.keys(risks);

      keys.forEach(key => {
        this.addRiskOptionsToSelects(key, risks[key]);
      });
    },
    addRiskOptionsToSelects(riskKey, risk) {
      const riskOptions = this.getRiskOptions(riskKey, risk);

      if (riskOptions.index) {
        this.indexOptions.push(riskOptions.index);
      }
      if (riskOptions.leo) {
        this.leoOptions.push(riskOptions.leo);
      }
      if (riskOptions.frequency) {
        this.frequencyOptions.push(riskOptions.frequency);
      }
    },
    updateClassifications() {
      if (!this.result?.length) return;

      const classifications = this.result.map(result => this.createClassificationFromResult(result));
      const recommendation = this.model.recommendation ||
                           this.classification?.recommendation ||
                           this.classification?.recomendation || '';

      this.model = { recommendation, classifications };
    },
    createClassificationFromResult(result) {
      const riskLevel = result.risk_level;
      const riskSettings = this.chemicalSettings.risks[riskLevel];

      if (!riskLevel || !riskSettings) {
        return {
          agent_chemical: result.agent_chemical || '',
          leo: null,
          index: null,
          frequency: null,
        };
      }
      const riskOptions = this.getRiskOptions(riskLevel, riskSettings);
      return {
        agent_chemical: result.agent_chemical || '',
        leo: riskOptions.leo || null,
        index: riskOptions.index || null,
        frequency: riskOptions.frequency || null,
      };
    },
    getRiskOptions(riskLevel, risk) {
      if (!risk) {
        return { index: null, leo: null, frequency: null };
      }

      const riskConfigs = {
        risk1: {
          index: `< ${risk.max}`,
          leo: `< ${this.formatPercentage(risk.max)}%`,
          frequency: 'Reavaliar quando ocorrer mudanças significativas no processo, lay-out, composição dos materiais ou produtos, sistemas de ventilação e outros',
        },
        risk2: {
          index: `${risk.min} a ${risk.max}`,
          leo: `${this.formatPercentage(risk.min)}% a ${this.formatPercentage(risk.max)}%`,
          frequency: `Bianualmente até reduzir para menor que ${this.formatPercentage(risk.min)}% do limite`,
        },
        risk3: {
          index: `${risk.min} a ${risk.max}`,
          leo: `${this.formatPercentage(risk.min)}% a ${this.formatPercentage(risk.max)}%`,
          frequency: `Semestralmente até reduzir para menor que ${this.formatPercentage(risk.min)}% do limite`,
        },
        risk4: {
          index: `> ${risk.min}`,
          leo: `> ${this.formatPercentage(risk.min)}%`,
          frequency: `Frequentemente até reduzir para < ${this.formatPercentage(risk.min)}% do limite`,
        },
      };

      return riskConfigs[riskLevel] || { index: null, leo: null, frequency: null };
    },
    toNumber(value) {
      if (!value) return null;
      const num = parseFloat(String(value).replace(',', '.'));
      return !Number.isNaN(num) && num !== 0 ? num : null;
    },
    formatPercentage(value) {
      const percentage = this.toNumber(value) * 100;
      return Number.isInteger(percentage) ? percentage : percentage.toFixed(2);
    },
  },
  mounted() {
    this.getSettings().then(() => {
      this.setModel();
      this.setSelectOptions();
    });
    Events.$on(_LOAD_MODEL_, () => {
      this.setModel();
    });
  },
  beforeDestroy() {
    Events.$off(_LOAD_MODEL_, this.setModel);
  },
  watch: {
    model: {
      handler(value) {
        this.$emit('update:classification', value);
      },
      deep: true,
    },
    result: {
      handler() {
        if (this.isInitialLoad) {
          this.isInitialLoad = false;
        } else {
          this.updateClassifications();
        }
      },
      deep: true,
    },
  },
};
</script>

<style lang="scss" scoped>
</style>
